import React, { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  Button,
  Card,
  CardTitle,
  CardBody,
  CardFooter,
  Icon,
  Modal,
  ModalBoxHeader,
  ModalBoxBody,
  Progress,
  ProgressSize,
  Title,
  Text,
} from '@patternfly/react-core';
import PlusCircleIcon from '@patternfly/react-icons/dist/esm/icons/plus-circle-icon';
import CheckCircleIcon from '@patternfly/react-icons/dist/esm/icons/check-circle-icon';
import { Table, Tbody, Td, Thead, Th, Tr } from '@patternfly/react-table';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { observer, useLocalStore } from 'mobx-react';
import { DateTime } from 'luxon';

import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import { storesContext } from 'stores';

import { useFeature } from '@ole-ui/ole-ui-components';
import ConnectivityTesterStore from './store';

import './styles.scss';

// hard-coded list of ids of the tests to run
// we want to skip screen-sharing
const testsToRun = [
  'webrtc-available',
  'browser-compatibility',
  'camera-access',
  'microphone-access',
  'device-enumeration',
  'data-channel',
  'webrtc-connection',
  // 'screen-sharing',
  'direct-connection',
  'turn-connection',
  'bandwidth',
  'jitter',
  'livekit-media-bandwidth',
  'turn-tcp-vs-udp',
  'webrtc-protocol-impact',
  'ice-candidate-discovery',
];

type TestResultType = {
  id: string;
  name: string;
  status: string;
  details: string;
  timestamp: string;
  duration: number;
};

const ConnectivityTesterIframe = observer(({ invokeIframe, tests }) => {
  const params = {
    tests,
    autostart: 'true',
  };
  const { configStore } = useContext(storesContext);
  const { bbbCompatibilityURL } = configStore;
  const sourceUrl = new URL(bbbCompatibilityURL);
  const searchParams = new URLSearchParams(params);
  sourceUrl.search = searchParams.toString();

  return (
    <div>
      {invokeIframe && (
        <iframe
          allow="camera; microphone; screenshare; autoplay; fullscreen; display-capture"
          src={sourceUrl.toString()}
          title="Compatibility tester"
          aria-hidden="true"
          tabIndex={-1}
          // We removed display='none' and height/width 0 so the user can see permission prompts!
          style={{
            height: '1px',
            width: '1px',
            border: 'none',
            position: 'absolute',
            opacity: 0.01,
          }}
        />
      )}
    </div>
  );
});

const TestResultTableRow = observer(({ result }) => {
  const testNames = {
    // BBB connectivity & compatibility tests
    'webrtc-available': 'WebRTC API Available',
    'browser-compatibility': 'Browser Compatibility',
    'camera-access': 'Camera Access',
    'microphone-access': 'Microphone Access',
    'device-enumeration': 'Media Device Enumeration',
    'data-channel': 'RTCDataChannel Support',
    'webrtc-connection': 'WebRTC Connection (LiveKit)',
    'screen-sharing': 'Screen Sharing Support',
    'direct-connection': 'Direct Connection Test',
    'turn-connection': 'TURN Connection Test',
    bandwidth: 'Bandwidth Test', // eslint-disable-line prettier/prettier
    jitter: 'Jitter Test (LiveKit)', // eslint-disable-line prettier/prettier
    'livekit-media-bandwidth': 'LiveKit Media Bandwidth Test',
    'turn-tcp-vs-udp': 'TURN TCP vs UDP Overhead Test',
    'webrtc-protocol-impact': 'WebRTC Protocol Impact Analysis',
    'ice-candidate-discovery': 'ICE Candidate Discovery',
    // client (lab environment connectivity & compatibility) tests
    'lab-bandwidth': 'Lab Bandwidth',
    latency: 'Latency', // eslint-disable-line prettier/prettier
    ws: 'WebSockets support', // eslint-disable-line prettier/prettier
    wss: 'Secure WebSockets support', // eslint-disable-line prettier/prettier
    resolution: 'Screen Resolution', // eslint-disable-line prettier/prettier
  };

  const cellStyle =
    result.status === 'success' ? 'result-cell-success' : 'result-cell-error';
  const iconStyle = result.status === 'success' ? 'icon-success' : 'icon-error';

  return (
    <Tr>
      <Td className={cellStyle} hasRightBorder>
        <span className="result-cell-contents">
          <span className="status-icon">
            <span>
              <Icon size="lg">
                {result.status === 'success' ? (
                  <CheckCircleIcon className={iconStyle} />
                ) : (
                  <PlusCircleIcon className={iconStyle} />
                )}
              </Icon>
            </span>
          </span>
          <span className="result-cell-text">
            <Title className="test-name" headingLevel="h3">
              {testNames[result.id]}
            </Title>
            <p className="result-details">{result.details}</p>
          </span>
        </span>
      </Td>
    </Tr>
  );
});

const FailedMessage = observer(() => {
  const { t } = useTranslation();

  return (
    <div className="connectivity__failed">
      <Text>{t('The test timed out. Please try again later.')}</Text>
    </div>
  );
});

const StatusCell = observer(({ summary }) => {
  const { t } = useTranslation();

  const status = () => {
    if (summary.failed === 0) {
      return {
        label: t('Fully Compatible'),
        circleClassName: 'circle circle-fully-compatible',
        spanClassName: 'span-fully-compatible',
      };
    }
    if (summary.success === 0) {
      return {
        label: t('Compatibility Issues'),
        circleClassName: 'circle circle-compatibilty-issues',
        spanClassName: 'span-compatibilty-issues',
      };
    }
    return {
      label: t('Limited Compatibility'),
      circleClassName: 'circle circle-limited-compatibility',
      spanClassName: 'span-limited-compatibility',
    };
  };

  const currentStatus = status();

  return (
    <div className="status-cell">
      <div className={currentStatus.circleClassName} />
      <span className={currentStatus.spanClassName}>{currentStatus.label}</span>
    </div>
  );
});

const Summary = observer(({ summary, browser }) => {
  const { t } = useTranslation();

  const columnNames = {
    status: t('Status'),
    date: t('Date'),
    browser: t('Browser'),
    platform: t('Platform'),
    total: t('All tests'),
    successful: t('Passed'),
    failed: t('Failed'),
  };

  return (
    <Table aria-label="Summary" isStriped>
      <Thead>
        <Tr>
          <Th>{columnNames.status}</Th>
          <Th>{columnNames.date}</Th>
          <Th>{columnNames.browser}</Th>
          <Th>{columnNames.platform}</Th>
          <Th>{columnNames.total}</Th>
          <Th>{columnNames.successful}</Th>
          <Th>{columnNames.failed}</Th>
        </Tr>
      </Thead>
      <Tbody>
        <Tr>
          <Td dataLabel={columnNames.status}>
            <StatusCell summary={summary} />
          </Td>
          <Td dataLabel={columnNames.date}>
            {DateTime.fromISO(summary.timestamp).toLocaleString(
              DateTime.DATETIME_MED,
            )}
          </Td>
          <Td dataLabel={columnNames.browser}>
            {browser.name} {browser.version}
          </Td>
          <Td dataLabel={columnNames.platform}>{browser.platform}</Td>
          <Td dataLabel={columnNames.total}>{summary.total}</Td>
          <Td dataLabel={columnNames.successful}>{summary.successful}</Td>
          <Td dataLabel={columnNames.failed}>{summary.failed}</Td>
        </Tr>
      </Tbody>
    </Table>
  );
});

const RunningModal = observer(({ isRunning, progress }) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isRunning}
      showClose={false}
      variant="small"
      aria-label={t('Connectivity test in progress')}
      ouiaId="BasicModal"
    >
      <ModalBoxHeader>
        <div className="connectivity-tester__modal-box-header">
          <span className="connectivity-tester__modal-box-header__title">
            {t('Connectivity test in progress')}
          </span>
        </div>
      </ModalBoxHeader>

      <ModalBoxBody>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <div tabIndex={0} role="group">
          {t(
            'Checking your connection to ensure everything works smoothly for your session. This may take a couple of minutes.',
          )}
          <div style={{ marginTop: '1.5rem' }}>
            <Progress
              value={progress}
              size={ProgressSize.sm}
              title={progress === 100 ? t('Complete') : t('In progress...')}
            />
          </div>
        </div>
      </ModalBoxBody>
    </Modal>
  );
});

const ConnectivityTesterResult = observer(({ store }) => {
  const { t } = useTranslation();

  const { configStore } = useContext(storesContext);
  const { bbbCompatibilityURL } = configStore;

  useEffect(() => {
    store.getLastTestResults();
  }, [store]);

  // The postMessage event listener
  useEffect(() => {
    const urlObject = new URL(bbbCompatibilityURL);
    const bbbCompatibilityOrigin = urlObject.origin;

    const handleMessage = (event: MessageEvent) => {
      // Security check: ensure we only listen to the BBB domain
      if (event.origin !== bbbCompatibilityOrigin) return;

      if (event.data?.type === 'BBB_TEST_RESULTS') {
        try {
          // We use a ternary operator just in case BBB ever updates their
          // code to send an actual object instead of a string.
          const parsedPayload =
            typeof event.data.data === 'string'
              ? JSON.parse(event.data.data)
              : event.data.data;

          store.processIframeResults(parsedPayload);
        } catch (error) {
          console.error('Failed to parse BBB test results:', error);
          store.handleFailure(
            'Received malformed data from compatibility tests.',
          );
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Cleanup function: remove the listener when the component unmounts
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [bbbCompatibilityURL, store]);

  const failedTests: TestResultType[] = store.testResult.tests?.filter(
    (result: TestResultType) => result.status === 'error',
  );
  const passedTests: TestResultType[] = store.testResult.tests?.filter(
    (result: TestResultType) => result.status === 'success',
  );

  return (
    <div>
      <RunningModal isRunning={store.running} progress={store.progress} />
      {store.failed && <FailedMessage />}
      <ConnectivityTesterIframe
        invokeIframe={store.invokeIframe}
        tests={testsToRun.join(',')}
      />
      {store.finished && (
        <div>
          <Title headingLevel="h2">{t('Test Results')}</Title>
          <Summary
            summary={store.testResult.summary}
            browser={store.testResult.browser}
          />

          {failedTests.length > 0 && (
            <span>
              <Title headingLevel="h2">Failed tests</Title>
              <Card className="card-results">
                <CardBody>
                  <Table className="results-table" isStriped>
                    <Tbody>
                      {failedTests.map((result: TestResultType) => (
                        <TestResultTableRow key={result.id} result={result} />
                      ))}
                    </Tbody>
                  </Table>
                </CardBody>
              </Card>
            </span>
          )}

          {passedTests.length > 0 && (
            <span>
              <Title headingLevel="h2">Passed tests</Title>
              <Card className="card-results">
                <CardBody>
                  <Table className="results-table" isStriped>
                    <Tbody>
                      {passedTests.map((result: TestResultType) => (
                        <TestResultTableRow key={result.id} result={result} />
                      ))}
                    </Tbody>
                  </Table>
                </CardBody>
              </Card>
            </span>
          )}
        </div>
      )}
    </div>
  );
});

const RunTestsButton = observer(
  ({ store }: { store: ConnectivityTesterStore }) => {
    const { t } = useTranslation();

    return (
      <Button
        variant="primary"
        onClick={() => {
          store.runTests();
        }}
        disabled={store.running}
      >
        {t('Run Connectivity Test')}
      </Button>
    );
  },
);

const Instructions = observer(
  ({ store }: { store: ConnectivityTesterStore }) => {
    const { t } = useTranslation();

    return (
      <Card className="instructions-card" isPlain>
        <CardTitle>
          <strong>
            {t(
              'Welcome to our connection test page for our virtual classroom!',
            )}
          </strong>
        </CardTitle>
        <CardBody>
          {t(
            'Test your Internet connection and browser compatibility. ' +
              'This will allow you to verify that your microphone, video, and speaker are all working smoothly. ' +
              'Please ensure that you have a good/stable network connection with UDP enabled and you are not using/utilizing any VPN or a restricted network. ' +
              'Red Hat highly recommends disconnection from a company VPN while taking our Training Classes, as these are known to cause connectivity issues.',
          )}
        </CardBody>
        <CardBody>
          {t(
            'During the test, your browser will ask to allow camera and microphone access. ' +
              'Please accept these prompts so the test can check your audio and video devices.',
          )}
        </CardBody>
        <CardFooter>
          <RunTestsButton store={store} />
        </CardFooter>
      </Card>
    );
  },
);

const ConnectivityTester = () => {
  const { configStore, userStore, routerStore } = useContext(storesContext);
  const { t } = useTranslation();
  const connectivityTesterStore = useLocalStore(
    () => new ConnectivityTesterStore(userStore, configStore),
  );

  const showConnectivityTester = useFeature({
    section: 'enablements',
    flagName: 'show_connectivity_tester',
    defaultFlagValue: false,
  });

  const routeTitle = (
    routerStore?.route as { params?: { title?: string } } | undefined
  )?.params?.title;

  return (
    <>
      <Helmet
        title={`${t(routeTitle ?? 'Connectivity Tester')} - Red Hat Learning`}
      />
      <span className="connectivity-tester__content">
        {showConnectivityTester && (
          <div>
            <Title headingLevel="h1">{t('Connectivity Test')}</Title>
            <Instructions store={connectivityTesterStore} />
            <ConnectivityTesterResult store={connectivityTesterStore} />
          </div>
        )}
      </span>
    </>
  );
};

export default withErrorBoundary(
  observer(ConnectivityTester),
  ComponentError,
  handleError,
);
